import React, { useEffect, useState } from 'react';
import {
  DataGrid
} from '@mui/x-data-grid';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Grid
} from '@mui/material';
import { legalTeamApi } from '../../api/api';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const ApprovedMarketUpdateGrid = () => {
  const userId = useSelector((state) => state.user.userId);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const fetchPendingUpdates = async () => {
    try {
      setLoading(true);
      const response = await legalTeamApi.getPendingMarketUpdates(userId);
      const formattedData = response.data.map((item, index) => ({
        id: item.Pid,
        propertyName: item.PropertyName,
        vendorName: item.vendorName,
        address: item.Address,
        city: item.City,
        sqftPrice: item.SqFtPrice,
        status: item.Status,
        Previousprice: item.PreviousSqFtPrice
      }));
      setRows(formattedData);
      setFilteredRows(formattedData);
    } catch (error) {
      console.error('Error fetching pending market updates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchPendingUpdates();
    }
  }, [userId]);

  // Filter logic
  useEffect(() => {
    const filtered = rows.filter((row) => {
      const matchesSearch =
        row.propertyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.vendorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.city.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'All' || row.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    setFilteredRows(filtered);
  }, [searchTerm, statusFilter, rows]);

  const handleApprove = (row) => {
    navigate('/marketupdate/approve', { state: { marketData: row } });
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'propertyName', headerName: 'Property Name', width: 180 },
    { field: 'vendorName', headerName: 'Vendor Name', width: 150 },
    { field: 'address', headerName: 'Address', width: 200 },
    { field: 'city', headerName: 'City', width: 120 },
    {
      field: 'sqftPrice',
      headerName: 'SqFt Price',
      width: 130,
      type: 'number'
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        let color = 'gray';
        if (params.value === 'Pending') color = 'orange';
        else if (params.value === 'Approved') color = 'green';
        else if (params.value === 'Rejected') color = 'red';

        return (
          <span style={{ color, fontWeight: 600 }}>{params.value}</span>
        );
      }
    },
    {
      field: 'approve',
      headerName: 'Action',
      width: 160,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => handleApprove(params.row)}
          disabled={params.row.status === 'Approved'}
        >
          Approve
        </Button>
      )
    }
  ];

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        Approve Market Updates
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, vendor, city..."
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            label="Filter by Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            fullWidth
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Approved">Approved</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
    
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSize={7}
          rowsPerPageOptions={[7]}
          disableSelectionOnClick
          autoHeight
        />

      )}
    </Box>
  );
};

export default ApprovedMarketUpdateGrid;
